/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";

import Pagination from "rc-pagination";

import {
  DetailsList,
  DetailsListLayoutMode,
  Dropdown,
  IColumn,
  IDetailsRowProps,
  SelectionMode,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";

import * as UserProfileActions from "Dataflows/UserProfile/UserProfileAction";

import ChevronIcon from "Components/Common/ChevronIcon/ChevronIcon";
import {
  detailListStyles,
  spinnerStyles,
  StyledSpan,
} from "Components/Common/SharedListStyles.styles";
import StatusElement from "Components/Common/Status/StatusElement";
import { AppState } from "Dataflows/Reducers";
import {
  getCurrentPage,
  getPageSize,
  getSearchText,
  isUserProfilesLoading,
} from "Dataflows/UserProfile/UserProfileSelector";

import { PaginationRow } from "../Common/Pagination/Pagination.styles";
import "../Common/Pagination/Pagination.styles.css";
import { IStudentListItem } from "./Interfaces/IStudentListItem";
import { IStudentListProps } from "./Interfaces/IStudentListProps";

/**
 * List of student profiles
 * @Component
 * @returns {React.FunctionComponent<IStudentListProps>}
 */
const StudentList: React.FunctionComponent<IStudentListProps> = (props: IStudentListProps) => {
  const history = useHistory();
  const { actions, data, filteredCount, isUserProfilesLoading, currentPage, pageSize } = props;
  const studentProfilesToRender = data.slice(0, pageSize);

  const formatStatusColumn = (statusKey: string): React.ReactElement => {
    return <StatusElement statusValue={statusKey} />;
  };

  const columns: IColumn[] = [
    {
      key: "id",
      name: "ID",
      ariaLabel: "Student ID",
      fieldName: "id",
      minWidth: 20,
      maxWidth: 36,
      isResizable: true,
    },
    {
      key: "firstName",
      name: "First Name",
      ariaLabel: "Student's first name",
      fieldName: "firstName",
      minWidth: 70,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: "lastName",
      name: "Last Name",
      ariaLabel: "Student's last name",
      fieldName: "lastName",
      minWidth: 70,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: "primaryEmail",
      name: "Primary Email",
      ariaLabel: "Student's email address",
      fieldName: "primaryEmail",
      minWidth: 120,
      maxWidth: 180,
      isResizable: true,
    },
    {
      key: "studentPartnerEmail",
      name: "Student Ambassadors Email",
      ariaLabel: "Student ambassadors email address",
      fieldName: "studentPartnerEmail",
      minWidth: 120,
      maxWidth: 180,
      isResizable: true,
    },
    {
      key: "cpm",
      name: "CPM",
      ariaLabel: "Community Program Manager",
      fieldName: "cpm",
      minWidth: 120,
      maxWidth: 180,
      isResizable: true,
    },
    {
      key: "level",
      name: "Milestone",
      ariaLabel: "Student's Milestone",
      fieldName: "levelStatus.id",
      onRender: (item: any) => (item as IStudentListItem).levelStatus?.levelName,
      minWidth: 65,
      maxWidth: 80,
      isResizable: true,
    },
    {
      key: "applicationStatus",
      name: "Application Status",
      ariaLabel: "Invitation status",
      fieldName: "application.applicationStatus",
      onRender: (item: any) =>
        formatStatusColumn((item as IStudentListItem).application?.applicationStatus),
      minWidth: 75,
      maxWidth: 80,
      isResizable: true,
    },
    {
      key: "programProfileStatusName",
      name: "Program Status",
      ariaLabel: "Program status",
      fieldName: "programProfileStatusName",
      onRender: (item: any) =>
        formatStatusColumn((item as IStudentListItem).programProfileStatusName),
      minWidth: 60,
      maxWidth: 80,
      isResizable: true,
    },
  ];

  const onClickStudentProfile = (studentId: number): void => {
    actions.updateSelectedUserProfileId(studentId);
    history.push(`/mlsa/details/${studentId}`);
  };

  const onRenderColumn = (
    item: IStudentListItem,
    index?: number,
    column?: IColumn
  ): React.ReactElement => {
    const fieldContent = item[column?.fieldName as keyof IStudentListItem] as string;

    if (column?.key == "id") {
      return <StyledSpan>{fieldContent}</StyledSpan>;
    }
    return <span>{fieldContent}</span>;
  };

  const onRenderRow = (props?: IDetailsRowProps, defaultRender?: any): React.ReactElement => {
    const studentId = (props!.item as IStudentListItem).id;
    return defaultRender!({
      ...props,
      onClick: () => onClickStudentProfile(studentId),
    });
  };

  if (isUserProfilesLoading) {
    return <Spinner size={SpinnerSize.large} styles={spinnerStyles} />;
  }

  return (
    <>
      <DetailsList
        items={studentProfilesToRender}
        columns={columns}
        selectionMode={SelectionMode.none}
        getKey={(item: any): string => `${(item as IStudentListItem).id}`}
        setKey="none"
        layoutMode={DetailsListLayoutMode.fixedColumns}
        isHeaderVisible={true}
        styles={detailListStyles}
        onRenderRow={onRenderRow}
        onRenderItemColumn={onRenderColumn}
        enterModalSelectionOnTouch={true}
      />
      <PaginationRow>
        <Pagination
          total={filteredCount}
          onChange={(page) => actions.updateCurrentPage(page)}
          showTitle={false}
          current={currentPage}
          prevIcon={ChevronIcon("ChevronLeft")}
          nextIcon={ChevronIcon("ChevronRight")}
          pageSize={pageSize}
          showSizeChanger
        />
        <Dropdown
          options={[
            { key: 16, text: "Show 16 results" },
            { key: 32, text: "Show 32 results" },
            { key: 48, text: "Show 48 results" },
          ]}
          onChange={(_e, op): void => {
            actions.updatePageSize(op?.key as number);
          }}
          selectedKey={pageSize}
          className="pagination-dropdown"
          title="Page Size Dropdown"
        />
      </PaginationRow>
    </>
  );
};

/**
 * Maps StudentsContainer to redux state
 * @param {AppState} state The App state.
 */
const mapStateToProps = (state: AppState, ownProps: IStudentListProps & any) => ({
  data: ownProps.data,
  isUserProfilesLoading: isUserProfilesLoading(state.userProfileState),
  currentPage: getCurrentPage(state.userProfileState),
  pageSize: getPageSize(state.userProfileState),
  searchText: getSearchText(state.userProfileState),
});

/**
 * Maps Component's dispatch/handler props to redux state
 * @param {Dispatch} dispatch The dispatch.
 */
const mapDispatchToProps = (dispatch: Dispatch<UserProfileActions.UserProfileActionTypes>) => ({
  actions: {
    updateCurrentPage: bindActionCreators(UserProfileActions.updateCurrentPage, dispatch),
    updatePageSize: bindActionCreators(UserProfileActions.updatePageSize, dispatch),
    updateSelectedUserProfileId: bindActionCreators(
      UserProfileActions.updateSelectedUserProfileId,
      dispatch
    ),
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(StudentList);
